import type {
	WebRTCConnectionEventCallback,
	WebRTCTalkSignalCallback,
	WebRTCTrackEventCallback,
} from '../types/webrtc-events';
import {
	ICE_GATHERING_TIMEOUT_MS,
	MAX_POLLS,
	NEGOTIATION_STRATEGY,
	POLL_DELAY_MS,
	RTC_CONFIGURATION,
} from './webrtc-config';
import { WebRTCEvents } from './webrtc-events';
import { sendCamRPC } from './webrtc-rpc';
import { webrtcTrace } from './webrtc-trace';
import { iceCandidatesToApiFormat, prepareICEServers, prepareRtcIceCandidates } from './webrtc-utils';

export class WebRTCClient {
	private deviceId: string;
	// Only consumed by the GetAnswer flow; the OfferRequest flow is single-stream.
	private streamId: number;
	private peerConnection: RTCPeerConnection | null = null;
	private stopped: boolean = false;

	private sessionId: string = '';
	private candidates: RTCIceCandidate[] = [];
	private isTrickle: boolean = false;
	// Backoff timer for the OfferRequest poll loop; cancelled in `stop()`.
	private pollTimeoutId: NodeJS.Timeout | null = null;
	private resolvePollDelay: (() => void) | null = null;

	private audioTrack: MediaStreamTrack | null = null;
	private audioSender: RTCRtpSender | null = null;
	private audioTransceiver: RTCRtpTransceiver | null = null;
	private micStream: MediaStream | null = null;
	private pttDataChannel: RTCDataChannel | null = null;

	// Resolved by the central `pc.onicegatheringstatechange` handler when
	// `iceGatheringState === 'complete'` under the new flow. `waitForIceGathering`
	// awaits this instead of reassigning the handler.
	private iceGatheringComplete: Promise<void> = Promise.resolve();
	private resolveIceGathering: () => void = () => {};

	public events = new WebRTCEvents();
	private errorMessage: string | null = null;

	constructor(
		deviceId: string,
		events: {
			onConnectionState?: WebRTCConnectionEventCallback;
			onTrack?: WebRTCTrackEventCallback;
			onTalkState?: WebRTCTalkSignalCallback;
		} = {},
		streamId: number = 0,
	) {
		this.deviceId = deviceId;
		this.streamId = streamId;
		if (events.onConnectionState) {
			this.events.registerConnectionEvent(events.onConnectionState);
		}
		if (events.onTrack) {
			this.events.registerTrackEvent(events.onTrack);
		}
		if (events.onTalkState) {
			this.events.registerTalkStateEvent(events.onTalkState);
		}

		this.start()
			.then(() => {
				console.log('[WebRTC] client started');
			})
			.catch((error) => {
				// `stop()` mid-negotiation (view closed / reconnect) is an
				// expected interruption, not a real failure — stay quiet.
				if (this.stopped) return;
				console.warn('[WebRTC] client start error', this.deviceId, error);
				this.errorMessage = error.message || 'Unknown error';
				this.events.triggerConnectionEvent(
					'start_error',
					this.peerConnection,
					this.errorMessage,
				);
			});
	}

	public get isTalkActive(): boolean {
		return this.audioTrack !== null && this.audioTrack.readyState === 'live';
	}

	public async startTalk(): Promise<void> {
		if (!this.audioSender || !this.pttDataChannel) {
			throw new Error('Talkback not negotiated for this stream');
		}
		if (this.pttDataChannel.readyState !== 'open') {
			throw new Error('PTT data channel not open yet');
		}
		if (this.isTalkActive) return;

		webrtcTrace('PTT', `${this.deviceId} · requesting microphone`);
		this.micStream = await navigator.mediaDevices.getUserMedia({
			audio: {
				channelCount: 1,
				echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true,
			},
			video: false,
		});
		this.audioTrack = this.micStream.getAudioTracks()[0] ?? null;
		webrtcTrace('PTT', `${this.deviceId} · microphone granted`, {
			track: this.audioTrack?.readyState,
			ice: this.peerConnection?.iceConnectionState,
			connection: this.peerConnection?.connectionState,
		});
		if (this.audioTrack) {
			await this.audioSender.replaceTrack(this.audioTrack);
		}

		webrtcTrace(
			'PTT',
			`${this.deviceId} · mic attached, sending ptt=on`,
			this.describeChannel(this.pttDataChannel),
		);
		// Resolution arrives as `{ ptt: 'granted' }` via dc.onmessage and is
		// surfaced through the talk-state callback — never set locally here.
		this.pttDataChannel.send(JSON.stringify({ action: 'ptt', state: 'on' }));
	}

	public async stopTalk(): Promise<void> {
		// Always tell the camera we are giving up the claim first, even if
		// confirmation never lands — local cleanup proceeds regardless.
		if (this.pttDataChannel?.readyState === 'open') {
			try {
				this.pttDataChannel.send(JSON.stringify({ action: 'ptt', state: 'off' }));
			} catch {
				/* swallow — we still need to clean up locally */
			}
		}
		try {
			await this.audioSender?.replaceTrack(null);
		} catch {
			/* sender may already be detached */
		}
		this.micStream?.getTracks().forEach((t) => t.stop());
		this.micStream = null;
		this.audioTrack = null;
	}

	public getPeerConnection(): RTCPeerConnection | null {
		return this.peerConnection;
	}
	public getErrorMessage(): string | null {
		return this.errorMessage;
	}

	private async start(): Promise<RTCPeerConnection> {
		return NEGOTIATION_STRATEGY === 'GetAnswer'
			? this.startGetAnswerFlow()
			: this.startOfferRequestFlow();
	}

	/**
	 * Client-initiated negotiation: build the offer locally, wait for ICE
	 * gathering to complete so all candidates land in the SDP, then ask the
	 * camera for the answer via `Streamer.GetAnswer`. The SDP also attaches a
	 * sendrecv mic sender and creates the `'ptt'` data channel before
	 * `createOffer` so push-to-talk is always negotiated; capability gating
	 * lives at the UI/device-resolution layer above this client.
	 */
	private async startGetAnswerFlow(): Promise<RTCPeerConnection> {
		this.iceGatheringComplete = new Promise<void>((resolve) => {
			this.resolveIceGathering = resolve;
		});

		const pc = new RTCPeerConnection(RTC_CONFIGURATION);
		this.peerConnection = pc;
		this.peerConnectionEventHandler(pc);

		pc.addTransceiver('video', { direction: 'recvonly' });

		// Single sendrecv audio m-section. The mic itself is attached on user
		// intent via `sender.replaceTrack(...)` in `startTalk()` — using
		// `addTrack` would create a second audio m-section the firmware rejects.
		this.audioTransceiver = pc.addTransceiver('audio', {
			direction: 'sendrecv',
		});
		this.audioSender = this.audioTransceiver.sender;

		// The data channel must be created BEFORE `createOffer` so it appears
		// in the SDP and the camera can wire its `onDataChannel` handler.
		this.pttDataChannel = pc.createDataChannel('ptt', { ordered: true });
		this.attachPttDataChannelHandlers(this.pttDataChannel);

		const offer = await pc.createOffer();
		if (!offer.sdp) {
			throw new Error('Failed to create offer SDP');
		}
		await pc.setLocalDescription(offer);

		const offerSdp = await this.waitForIceGathering(pc);

		// `stop()` may have run during offer creation or ICE gathering
		if (this.stopped || this.peerConnection !== pc) {
			throw new Error('WebRTC negotiation aborted: connection was stopped');
		}

		const ice_servers = prepareICEServers(RTC_CONFIGURATION.iceServers);
		const answerFrame: { sdp: string; session_id?: string } = (await sendCamRPC(
			this.deviceId,
			'Streamer.GetAnswer',
			{
				sdp: offerSdp,
				ice_servers,
				stream_id: this.streamId,
			},
		)) as any;

		// Answering cost the camera one of its three stream slots, so the id is
		// read out before anything can bail.
		const sessionId = answerFrame?.session_id ?? '';

		// `stop()` may have run while the GetAnswer RPC was in flight (view
		// closed or reconnect). Bail before touching the detached connection,
		// handing back the slot on the way out: nothing will ever attach to it,
		// and a slot this client does not hold is a slot only the camera's own
		// timeout can reclaim.
		if (this.stopped || this.peerConnection !== pc) {
			this.releaseCameraSession(sessionId);
			throw new Error('WebRTC negotiation aborted: connection was stopped');
		}

		// Claimed once this negotiation is the one that owns the connection, so
		// `stop()` releases it and a superseded negotiation cannot clobber it.
		// Before the SDP check, so a slot handed out with an unusable answer is
		// still released by the teardown that follows.
		this.sessionId = sessionId;

		if (!answerFrame?.sdp) {
			throw new Error('No SDP received from Streamer.GetAnswer');
		}

		await pc.setRemoteDescription({ type: 'answer', sdp: answerFrame.sdp });

		webrtcTrace('Client', `${this.deviceId} · negotiated`, {
			sessionId: this.sessionId,
			audioDirection: this.audioTransceiver?.currentDirection,
		});
		return pc;
	}

	/**
	 * Device-initiated negotiation (preserved verbatim from the legacy flow):
	 * the camera sends the offer via `streamer.Offer`, we answer, and trickle
	 * candidates back via `streamer.Trickle` / `streamer.Poll`.
	 *
	 * Kept reachable via the `NEGOTIATION_STRATEGY` switch so we can revert
	 * per-device if needed.
	 */
	private async startOfferRequestFlow(): Promise<RTCPeerConnection> {
		const pc = new RTCPeerConnection(RTC_CONFIGURATION);
		this.peerConnection = pc;

		this.peerConnectionEventHandler(pc);

		// This flow needs the recvonly video transceiver before
		// `setRemoteDescription` so the answer matches the offer's m-lines.
		pc.addTransceiver('video', { direction: 'recvonly' });

		const ice_servers = prepareICEServers(RTC_CONFIGURATION.iceServers);

		const offerFrame: {
			sdp: string;
			session_id: string;
			candidates: any[]; //@ToDo: Typizating
			end_of_candidates: boolean;
		} = (await sendCamRPC(this.deviceId, 'streamer.Offer', {
			ice_servers,
		})) as any;

		// Offering cost the camera one of its three stream slots, so the id is
		// read out before anything can bail.
		const sessionId = offerFrame?.session_id ?? '';

		// `stop()` may have run while the Offer RPC was in flight (view closed
		// or reconnect). Bail before touching the detached connection, handing
		// back the slot on the way out — nothing will ever attach to it.
		if (this.stopped || this.peerConnection !== pc) {
			this.releaseCameraSession(sessionId);
			throw new Error('WebRTC negotiation aborted: connection was stopped');
		}

		this.sessionId = sessionId;
		if (!offerFrame?.sdp) {
			throw new Error('No SDP received from the device');
		}

		this.isTrickle = !offerFrame?.end_of_candidates;

		await pc.setRemoteDescription({
			type: 'offer',
			sdp: offerFrame?.sdp,
		});

		if (Array.isArray(offerFrame?.candidates)) {
			const candidates = prepareRtcIceCandidates(offerFrame.candidates);
			candidates.forEach((c) => pc.addIceCandidate(c));
		}

		const answer = await pc.createAnswer();
		const answerSdp = answer?.sdp;

		if (!answerSdp) {
			throw new Error('No Answer SDP available');
		}

		// Force the answer's DTLS role to `passive`. The device firmware advertises
		// `a=setup:actpass` but its DTLS stack always acts as the client (`active`),
		// so we must take the server (`passive`) role for the handshake to complete.
		// Without this, ICE reaches `connected` but DTLS deadlocks at `connecting`.
		if (answer.sdp) {
			answer.sdp = answer.sdp.replace(/a=setup:active/g, 'a=setup:passive');
		}

		await pc.setLocalDescription(answer);

		if (this.isTrickle) {
			await this.handleTrickle(answerSdp);
		}

		return pc;
	}

	/**
	 * Resolves with the local SDP once ICE gathering completes, or after
	 * `timeoutMs` has elapsed (whichever comes first). Listens via the shared
	 * `iceGatheringComplete` promise resolved by the central
	 * `pc.onicegatheringstatechange` handler — does not reassign the handler.
	 */
	private async waitForIceGathering(
		pc: RTCPeerConnection,
		timeoutMs: number = ICE_GATHERING_TIMEOUT_MS,
	): Promise<string> {
		const startedAt = Date.now();
		console.log(
			`[WebRTC] ICE gathering start: state=${pc.iceGatheringState}, candidates=${this.candidates.length}, timeoutMs=${timeoutMs}`,
		);
		if (pc.iceGatheringState !== 'complete') {
			let timedOut = false;
			let timer: ReturnType<typeof setTimeout> | undefined;
			await Promise.race([
				this.iceGatheringComplete,
				new Promise<void>((resolve) => {
					timer = setTimeout(() => {
						timedOut = true;
						resolve();
					}, timeoutMs);
				}),
			]);
			clearTimeout(timer);
			if (timedOut) {
				console.warn(
					`[WebRTC] ICE gathering timed out after ${Date.now() - startedAt}ms — state=${pc.iceGatheringState}, candidates=${this.candidates.length}. Proceeding with what we have.`,
				);
			}
		}
		const sdp = pc.localDescription?.sdp ?? '';
		console.log(
			`[WebRTC] ICE gathering done after ${Date.now() - startedAt}ms — candidates=${this.candidates.length}, sdpCandidates=${(sdp.match(/^a=candidate:/gm) ?? []).length}`,
		);
		return sdp;
	}

	/**
	 * Wires the `'ptt'` data channel lifecycle to talk signals. The camera
	 * owns the claim: `granted` / `released` are emitted only on the camera's
	 * confirmation frames, never assumed locally from user intent.
	 */
	private attachPttDataChannelHandlers(dc: RTCDataChannel) {
		dc.onopen = () => {
			webrtcTrace('PTT', `${this.deviceId} · channel open`, this.describeChannel(dc));
			this.events.triggerTalkStateChange('ready');
		};
		dc.onclose = () => {
			webrtcTrace('PTT', `${this.deviceId} · channel closed`, this.describeChannel(dc));
			this.events.triggerTalkStateChange('closed');
		};
		dc.onerror = (event) => {
			const error = (event as RTCErrorEvent).error;
			webrtcTrace('PTT', `${this.deviceId} · channel error`, {
				message: error?.message,
				detail: error?.errorDetail,
				sctpCauseCode: error?.sctpCauseCode,
				...this.describeChannel(dc),
			});
		};
		dc.onmessage = (evt) => {
			// Traced raw and before parsing: a frame explaining a refusal need
			// not be the JSON shape we expect, and swallowing it is what makes a
			// rejection look identical to the camera closing for no reason.
			webrtcTrace('PTT', `${this.deviceId} · camera frame`, { raw: evt.data });
			try {
				const msg = JSON.parse(evt.data);
				if (msg?.ptt === 'granted') {
					this.events.triggerTalkStateChange('granted');
				} else if (msg?.ptt === 'released') {
					this.events.triggerTalkStateChange('released');
				}
			} catch {
				/* ignore malformed frames */
			}
		};
	}

	/**
	 * Transport-level snapshot of the `ptt` channel. The SCTP state separates a
	 * camera that closed just this channel from one that dropped the whole
	 * bundled transport underneath it.
	 */
	private describeChannel(dc: RTCDataChannel) {
		return {
			channel: dc.readyState,
			sctp: this.peerConnection?.sctp?.state,
			connection: this.peerConnection?.connectionState,
			audioDirection: this.audioTransceiver?.currentDirection,
		};
	}

	/**
	 * Tears down the stream and releases the mic. Closes the peer connection
	 * immediately rather than lingering to flush a final PTT `off`: session
	 * teardown is owned by the `streamer.stopstream` RPC, and a lingering PC
	 * would collide with the new session on reconnect.
	 */
	public stop() {
		webrtcTrace('Client', `${this.deviceId} · stop`, {
			ice: this.peerConnection?.iceConnectionState,
			connection: this.peerConnection?.connectionState,
			sessionId: this.sessionId,
		});
		this.stopped = true;
		this.stopTalk().catch(() => {
			/* swallow — we still need to tear down the PC */
		});

		// Best-effort native teardown. None of these throw in normal flows;
		// the single guard just covers an object that is already torn down.
		try {
			this.pttDataChannel?.close();
			this.peerConnection?.getTransceivers?.().forEach((t) => t.stop?.());
			this.peerConnection?.getSenders().forEach((s) => s.track?.stop());
			this.peerConnection?.close();
		} catch {
			/* already torn down */
		}

		this.clearPollTimer();
		// Unblock a pending waitForIceGathering race
		this.resolveIceGathering();
		this.stopRemoteStream();

		this.pttDataChannel = null;
		this.audioTransceiver = null;
		this.audioSender = null;
		this.peerConnection = null;
		this.candidates = [];
	}

	/**
	 * Hands one camera-side stream slot back. Takes the id rather than reading
	 * it off the instance so an abandoned negotiation can release the slot it
	 * was given without touching `this.sessionId`, which by then may already
	 * belong to the negotiation that replaced it.
	 *
	 * The RPC is deliberately not awaited — `stop()` runs synchronously from
	 * `beforeunload`, where the socket frame is written during the handler but
	 * the response never arrives.
	 */
	private releaseCameraSession(sessionId: string | undefined) {
		if (!sessionId?.length) {
			return;
		}
		sendCamRPC(this.deviceId, 'streamer.stopstream', { session_id: sessionId }).catch((e) => {
			console.warn('[WebRTC] streamer.stopstream failed', this.deviceId, e);
		});
	}

	/**
	 * Releases this client's stream slot. Idempotent: the session id is cleared
	 * so a later `stop()` cannot send a second `streamer.stopstream` for it.
	 */
	private stopRemoteStream() {
		const sessionId = this.sessionId;
		this.sessionId = '';
		this.releaseCameraSession(sessionId);
	}

	private peerConnectionEventHandler(pc: RTCPeerConnection) {
		pc.onicecandidate = (e) => {
			if (e.candidate) {
				this.candidates.push(e.candidate);
			}
		};

		pc.onconnectionstatechange = () => {
			// Throwing inside a DOM event handler is swallowed by the browser, so
			// surface the failure through the event bus where the UI can react.
			if (pc.connectionState === 'failed' && !this.stopped) {
				this.events.triggerConnectionEvent('start_error', pc, 'WebRTC connection failed');
			}
		};

		pc.oniceconnectionstatechange = (event: Event) => {
			this.events.triggerConnectionEvent('oniceconnectionstatechange', pc, event);
		};

		pc.onicegatheringstatechange = (event: Event) => {
			this.events.triggerConnectionEvent('onicegatheringstatechange', pc, event);
			if (NEGOTIATION_STRATEGY === 'GetAnswer') {
				if (pc.iceGatheringState === 'complete') {
					this.resolveIceGathering();
				}
			} else {
				this.handleIceGatheringStateChange();
			}
		};

		pc.ontrack = this.events.triggerTrackEvent.bind(this.events);
	}

	private handleIceGatheringStateChange() {
		const pc = this.peerConnection;
		if (!pc || this.stopped) return;

		if (this.isTrickle) {
			return sendCamRPC(this.deviceId, 'streamer.Trickle', {
				session_id: this.sessionId,
				candidates: iceCandidatesToApiFormat(this.candidates),
				end_of_candidates: true,
			});
		}

		if (pc.iceGatheringState === 'complete') {
			sendCamRPC(this.deviceId, 'streamer.Answer', {
				session_id: this.sessionId,
				sdp: pc.localDescription?.sdp,
				end_of_candidates: true,
			});
		}
	}

	private async handleTrickle(answerSdp: string) {
		if (!this.isTrickle) {
			return;
		}

		sendCamRPC(this.deviceId, 'streamer.Answer', {
			session_id: this.sessionId,
			sdp: answerSdp,
		});

		this.pollCandidates();
	}

	private async pollCandidates() {
		for (let i = 0; i < MAX_POLLS; i++) {
			if (this.stopped) break;
			const hasEnded = await this.pollFromDevice();
			if (hasEnded || this.stopped) break;
			// Linear backoff: each poll waits longer than the last. The handle is
			// tracked so `stop()` can cancel an in-flight wait.
			await new Promise<void>((resolve) => {
				this.resolvePollDelay = resolve;
				this.pollTimeoutId = setTimeout(resolve, POLL_DELAY_MS * i);
			});
			this.pollTimeoutId = null;
			this.resolvePollDelay = null;
		}
	}

	/** Cancels an in-flight poll backoff and unblocks its awaiter so the loop can exit. */
	private clearPollTimer() {
		if (this.pollTimeoutId) {
			clearTimeout(this.pollTimeoutId);
			this.pollTimeoutId = null;
		}
		this.resolvePollDelay?.();
		this.resolvePollDelay = null;
	}

	private async pollFromDevice() {
		const pollFrame = await sendCamRPC(this.deviceId, 'streamer.Poll', {
			session_id: this.sessionId,
		});

		const pc = this.peerConnection;
		if (!pc || this.stopped) return true;

		if (!Array.isArray(pollFrame?.candidates)) {
			return true; //@ToDo: This might need to be false, in order to continue polling
		}
		const candidates = prepareRtcIceCandidates(pollFrame.candidates);

		candidates.map(async (candidate) => {
			await pc.addIceCandidate(candidate);
		});

		return typeof pollFrame?.end_of_candidates === 'boolean' && pollFrame.end_of_candidates;
	}
}
